import { z } from 'zod';

const isEmpty = (value?: string | null) =>
  value === undefined || value === null || value.trim() === '';

export const siteFormSchema = z
  .object({
    customer_id: z.string().optional(),
    address: z.string().min(1, "L'indirizzo del sito è obbligatorio"),
    iso_50001_certified_site: z.boolean().nullable().optional(),
    covered_area: z.any().optional().nullable(),
    telecom_network_presence: z.boolean().nullable(),
    cogeneration_availability: z.boolean().nullable(),

    // Inquadramento sito: paragrafo descrittivo (manuale o via AI), max ~mezza pagina
    site_framing: z.string().max(3000, 'Massimo 3000 caratteri (circa mezza pagina)').optional(),

    // Boundaries
    excluded_areas: z.string().optional(),
    norm_unicei_16247_1: z.boolean().optional(),
    norm_unicei_16247_2: z.boolean().optional(),
    norm_unicei_16247_3: z.boolean().optional(),
    norm_unicei_16247_4: z.boolean().optional(),
    notes: z.string().optional(),

    // Properties
    property: z.string().optional(),
    type: z.string().optional(),
    availability_until: z.date().optional().nullable(),
    construction_year: z.date().optional().nullable(),
    renovation_year: z.date().optional().nullable(),
    energy_performance_certificate: z.boolean().optional(),
    efficiency_class: z.string().optional(),
    building_envelope_walls_floors: z.string().optional(),
    building_envelope_insulation: z.string().optional(),
    building_envelope_transmittance: z.string().optional(),
    fixtures_glass: z.string().optional(),
    fixtures_frame: z.string().optional(),
    fixtures_transmittance: z.string().optional(),
    fixtures_shading: z.string().optional(),
    img: z.string().optional().nullable(),

    // Climatic fields
    climatic_zone: z.string().max(1).optional(),
    start_summer: z.string().max(5).optional(),
    start_winter: z.string().max(5).optional(),

    // Counter counts
    has_podp: z.number().int().min(0).optional(),
    has_podi: z.number().int().min(0).optional(),
    has_prod: z.number().int().min(0).optional(),
    has_pdr: z.number().int().min(0).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.norm_unicei_16247_2) {
      const requiredFields: Array<{ field: keyof typeof data; message: string }> = [
        {
          field: 'property',
          message: 'Il campo Proprietà è obbligatorio quando la UNI CEI 16247-2 è selezionata',
        },
        {
          field: 'type',
          message: 'Il campo Tipologia è obbligatorio quando la UNI CEI 16247-2 è selezionata',
        },
        {
          field: 'efficiency_class',
          message: 'La classe di efficienza è obbligatoria quando la UNI CEI 16247-2 è selezionata',
        },
      ];

      requiredFields.forEach(({ field, message }) => {
        const value = data[field];
        if (typeof value === 'string' && isEmpty(value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path: [field],
          });
        }
      });

      const dateFields: Array<{ field: keyof typeof data; message: string }> = [
        {
          field: 'construction_year',
          message: "L'anno di costruzione è obbligatorio quando la UNI CEI 16247-2 è selezionata",
        },
        {
          field: 'availability_until',
          message: 'La disponibilità è obbligatoria quando la UNI CEI 16247-2 è selezionata',
        },
      ];

      dateFields.forEach(({ field, message }) => {
        const value = data[field];
        if (value === null || value === undefined || value === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path: [field],
          });
        }
      });
    }
  });

export type SiteFormData = z.infer<typeof siteFormSchema>;
